import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { MAX_PREVIEW_SIDE, MAX_CANVAS_SIDE } from "../constants";
import {
  composePdfViewportRotation,
  getRotatedSize,
} from "../../FloorPlanPanelWrapper/createDisplayTransform";
import type {
  PdfState,
  UsePdfCanvasRendererArgs,
  UsePdfCanvasRendererResult,
  TileRegion,
} from "./types";
import {
  cancelRenderTask,
  destroyPdfDocument,
  cancelAllInFlight,
  isRenderCancelledError,
} from "./utils";

pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

const OVERSCAN_MULTIPLIER = 2.0;

const INITIAL_PDF_STATE: PdfState = {
  page: null,
  document: null,
  loadingTask: null,
  worker: null,
  renderTask: null,
  previewRenderTask: null,
  currentUrl: null,
  offscreenCanvas: null,
};

/**
 * Two-canvas PDF renderer with zoom/pan support.
 *
 * - **Preview canvas** (background): Renders the full page once at low
 *   resolution. Uses CSS transforms for instant zoom/pan. Always shows the
 *   entire page (blurry when zoomed in, but corners always visible).
 *
 * - **Foreground canvas**: Container-sized, renders the visible viewport at
 *   screen resolution via an offscreen canvas + atomic swap. Provides the
 *   crisp high-res layer on top.
 */
export const usePdfCanvasRenderer = ({
  canvasRef,
  previewCanvasRef,
  containerRef,
  pdfUrl,
  pageNumber,
  zoomLevel,
  panOffset,
  rotation,
}: UsePdfCanvasRendererArgs): UsePdfCanvasRendererResult => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [basePageWidth, setBasePageWidth] = useState(0);
  const [basePageHeight, setBasePageHeight] = useState(0);
  const [tileRegion, setTileRegion] = useState<TileRegion | null>(null);

  const pdfStateRef = useRef<PdfState>({ ...INITIAL_PDF_STATE });

  // Keep current zoom/pan in refs so renderVisibleArea has a stable identity
  const zoomLevelRef = useRef(zoomLevel);
  zoomLevelRef.current = zoomLevel;
  const panOffsetRef = useRef(panOffset);
  panOffsetRef.current = panOffset;
  const rotationRef = useRef(rotation);
  rotationRef.current = rotation;
  const drawnRotationRef = useRef(rotation);

  // Derived, not stored: the page box swaps in the same render the rotation prop changes.
  const { width: pageWidth, height: pageHeight } = useMemo(
    () =>
      getRotatedSize({
        rotation,
        width: basePageWidth,
        height: basePageHeight,
      }),
    [rotation, basePageWidth, basePageHeight],
  );

  const viewportRotation = useCallback(
    (page: pdfjsLib.PDFPageProxy) =>
      composePdfViewportRotation({
        pageRotate: page.rotate,
        rotation: rotationRef.current,
      }),
    [],
  );

  // Render full page at low resolution to the preview (background) canvas.
  // Called once when the PDF loads. CSS transforms handle zoom/pan.
  const renderPreview = useCallback(async () => {
    const previewCanvas = previewCanvasRef.current;
    const page = pdfStateRef.current.page;
    if (!previewCanvas || !page) return;

    const baseViewport = page.getViewport({
      scale: 1,
      rotation: viewportRotation(page),
    });
    const naturalWidth = baseViewport.width;
    const naturalHeight = baseViewport.height;

    const previewScale = Math.min(
      MAX_PREVIEW_SIDE / naturalWidth,
      MAX_PREVIEW_SIDE / naturalHeight,
      1,
    );

    const devicePixelRatio = window.devicePixelRatio || 1;
    const effectiveScale = previewScale * devicePixelRatio;

    const maxEffectiveScale = Math.min(
      MAX_PREVIEW_SIDE / naturalWidth,
      MAX_PREVIEW_SIDE / naturalHeight,
    );
    const clampedScale = Math.min(effectiveScale, maxEffectiveScale);

    previewCanvas.width = Math.round(naturalWidth * clampedScale);
    previewCanvas.height = Math.round(naturalHeight * clampedScale);
    previewCanvas.style.width = `${naturalWidth}px`;
    previewCanvas.style.height = `${naturalHeight}px`;

    const previewViewport = page.getViewport({
      scale: clampedScale,
      rotation: viewportRotation(page),
    });
    const context = previewCanvas.getContext("2d");
    if (!context) return;

    try {
      const state = pdfStateRef.current;
      await cancelRenderTask(state, "previewRenderTask");

      const renderTask = page.render({
        canvasContext: context,
        canvas: previewCanvas,
        viewport: previewViewport,
      });
      state.previewRenderTask = renderTask;
      await renderTask.promise;
      state.previewRenderTask = null;
    } catch (error: unknown) {
      if (isRenderCancelledError(error)) return;
      console.error("PDF preview render failed:", error);
    }
  }, [previewCanvasRef, viewportRotation]);

  // Render the visible viewport at screen resolution to the foreground canvas.
  // Uses offscreen canvas + atomic swap to prevent blank flashes.
  // Stable identity — reads zoom/pan from refs.
  const renderVisibleArea = useCallback(async () => {
    const visibleCanvas = canvasRef.current;
    const container = containerRef.current;
    const state = pdfStateRef.current;
    if (!visibleCanvas || !container || !state.page) return;

    await cancelRenderTask(state, "renderTask");

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    if (containerWidth === 0 || containerHeight === 0) return;

    const devicePixelRatio = window.devicePixelRatio || 1;

    const zoom = zoomLevelRef.current;
    const pan = panOffsetRef.current;

    const baseViewport = state.page.getViewport({
      scale: 1,
      rotation: viewportRotation(state.page),
    });
    const fitZoom = Math.min(
      containerWidth / baseViewport.width,
      containerHeight / baseViewport.height,
      1,
    );

    const overscanMultiplier = zoom > fitZoom ? OVERSCAN_MULTIPLIER : 1.0;

    const overscanPadX = (containerWidth * (overscanMultiplier - 1)) / 2;
    const overscanPadY = (containerHeight * (overscanMultiplier - 1)) / 2;

    const renderWidth = containerWidth + 2 * overscanPadX;
    const renderHeight = containerHeight + 2 * overscanPadY;

    const pixelWidth = Math.min(
      Math.round(renderWidth * devicePixelRatio),
      MAX_CANVAS_SIDE,
    );
    const pixelHeight = Math.min(
      Math.round(renderHeight * devicePixelRatio),
      MAX_CANVAS_SIDE,
    );

    const effectiveRenderWidth = pixelWidth / devicePixelRatio;
    const effectiveRenderHeight = pixelHeight / devicePixelRatio;
    const effectiveOverscanX = (effectiveRenderWidth - containerWidth) / 2;
    const effectiveOverscanY = (effectiveRenderHeight - containerHeight) / 2;

    if (!state.offscreenCanvas) {
      state.offscreenCanvas = document.createElement("canvas");
    }

    const offscreen = state.offscreenCanvas;
    offscreen.width = pixelWidth;
    offscreen.height = pixelHeight;

    const offscreenContext = offscreen.getContext("2d");
    if (!offscreenContext) return;

    offscreenContext.clearRect(0, 0, pixelWidth, pixelHeight);

    const viewport = state.page.getViewport({
      scale: zoom * devicePixelRatio,
      rotation: viewportRotation(state.page),
      offsetX: (pan.x + effectiveOverscanX) * devicePixelRatio,
      offsetY: (pan.y + effectiveOverscanY) * devicePixelRatio,
    });

    try {
      const renderTask = state.page.render({
        canvasContext: offscreenContext,
        canvas: offscreen,
        viewport,
      });
      state.renderTask = renderTask;
      await renderTask.promise;
      state.renderTask = null;

      // Atomic swap onto visible canvas
      // Only resize pixel buffer when dimensions change — setting width/height clears the canvas
      if (
        visibleCanvas.width !== pixelWidth ||
        visibleCanvas.height !== pixelHeight
      ) {
        visibleCanvas.width = pixelWidth;
        visibleCanvas.height = pixelHeight;
      }

      const visibleContext = visibleCanvas.getContext("2d");
      if (visibleContext) {
        visibleContext.drawImage(offscreen, 0, 0);
      }

      // Set CSS size in page units — the parent wrapper's scale(zoom) converts to screen pixels
      const tilePageWidth = effectiveRenderWidth / zoom;
      const tilePageHeight = effectiveRenderHeight / zoom;
      const tilePageLeft = -(pan.x + effectiveOverscanX) / zoom;
      const tilePageTop = -(pan.y + effectiveOverscanY) / zoom;

      visibleCanvas.style.width = `${tilePageWidth}px`;
      visibleCanvas.style.height = `${tilePageHeight}px`;

      setTileRegion({
        pageLeft: tilePageLeft,
        pageTop: tilePageTop,
        pageWidth: tilePageWidth,
        pageHeight: tilePageHeight,
      });
    } catch (error: unknown) {
      if (isRenderCancelledError(error)) return;
      console.error("PDF render failed:", error);
    }
  }, [canvasRef, containerRef, viewportRotation]);

  // A turned page needs both canvases redrawn; the document itself stays loaded.
  useEffect(() => {
    if (!isLoaded || !pdfStateRef.current.page) return;
    // A new url is about to load; that load draws at the current rotation itself.
    if (pdfStateRef.current.currentUrl !== pdfUrl) return;
    if (drawnRotationRef.current === rotation) return;
    drawnRotationRef.current = rotation;
    let cancelled = false;
    const redraw = async () => {
      // Drop the old-orientation tile so only the preview shows until the new tile lands.
      setTileRegion(null);
      const visibleCanvas = canvasRef.current;
      const visibleContext = visibleCanvas?.getContext("2d");
      if (visibleCanvas && visibleContext) {
        visibleContext.clearRect(
          0,
          0,
          visibleCanvas.width,
          visibleCanvas.height,
        );
      }
      await renderPreview();
      if (cancelled) return;
      await renderVisibleArea();
    };
    redraw();
    return () => {
      cancelled = true;
    };
  }, [rotation, isLoaded, pdfUrl, canvasRef, renderPreview, renderVisibleArea]);

  // Load PDF document and page, then render preview
  useEffect(() => {
    let cancelled = false;
    const state = pdfStateRef.current;

    const loadDocument = async () => {
      // Same document, different page only
      if (state.currentUrl === pdfUrl && state.document) {
        try {
          if (state.page) {
            state.page.cleanup();
            state.page = null;
          }

          const page = await state.document.getPage(pageNumber);
          if (cancelled) return;

          state.page = page;
          const baseViewport = page.getViewport({ scale: 1 });
          setBasePageWidth(baseViewport.width);
          setBasePageHeight(baseViewport.height);
          drawnRotationRef.current = rotationRef.current;
          await renderPreview();
          setIsLoaded(true);
        } catch (error) {
          if (cancelled) return;
          console.error("Failed to get PDF page:", error);
        }
        return;
      }

      // New URL — destroy old document and load fresh
      try {
        setIsLoaded(false);
        setTileRegion(null);

        // Clear foreground canvas so the preview layer is visible during load
        const visibleCanvas = canvasRef.current;
        if (visibleCanvas) {
          const context = visibleCanvas.getContext("2d");
          context?.clearRect(0, 0, visibleCanvas.width, visibleCanvas.height);
        }

        await destroyPdfDocument(state);
        if (cancelled) return;

        const worker = new pdfjsLib.PDFWorker();
        state.worker = worker;

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          worker,
        });
        state.loadingTask = loadingTask;

        const pdfDocument = await loadingTask.promise;
        state.loadingTask = null;

        if (cancelled) {
          pdfDocument.destroy();
          return;
        }

        state.document = pdfDocument;
        state.currentUrl = pdfUrl;

        // Clamp to valid range — pageNumber may exceed page count when image
        // filenames use 1-based numbering or skip cover pages.
        const safePageNumber = Math.min(
          Math.max(pageNumber, 1),
          pdfDocument.numPages,
        );
        const page = await pdfDocument.getPage(safePageNumber);
        if (cancelled) return;

        state.page = page;
        const baseViewport = page.getViewport({ scale: 1 });
        setBasePageWidth(baseViewport.width);
        setBasePageHeight(baseViewport.height);
        drawnRotationRef.current = rotationRef.current;
        await renderPreview();
        setIsLoaded(true);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load PDF:", error);
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      cancelAllInFlight(state);
    };
  }, [pdfUrl, pageNumber, canvasRef, renderPreview]);

  // Cleanup on unmount
  useEffect(() => {
    const state = pdfStateRef.current;
    return () => {
      destroyPdfDocument(state);
      if (state.offscreenCanvas) {
        state.offscreenCanvas.width = 0;
        state.offscreenCanvas.height = 0;
        state.offscreenCanvas = null;
      }
    };
  }, []);

  return {
    isLoaded,
    pageWidth,
    pageHeight,
    tileRegion,
    render: renderVisibleArea,
  };
};
